import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { GatsbyImage } from "gatsby-plugin-image";
import { Flex, Box, Divider, Text, Image, Link } from "theme-ui";
import { StarRating } from "../atoms";
import { typography, colors } from "../../styles/theme";
import icon from "../../../static/images/comparo/backIcon.png";

const StyledLink = styled(Link)`
  text-decoration: none;
  height: 100%;
  &:hover {
    text-decoration: none;
  }
`;

const TestimonialWrapper = styled.div`
  max-width: fit-content;
  margin: 0 auto;
  cursor: pointer;
  .testimonial-content {
    height: 100%;
  }
`;

const Testimonial = ({
  variant,
  subVariant,
  backgroundColor,
  image,
  imageUrl,
  reviewUrl,
  imageAltText,
  feedback,
  employeeName,
  employeeDesignation,
  rating,
  companyName,
  companyLogo,
  companyLogoUrl,
  companyLogoAltText,
  customStyles,
  wrapper1CustomeStyles,
  wrapper2CustomeStyles,
  innerContainerCustomStyles
}) => {
  let renderedComponent = null;

  if (variant === "default") {
    renderedComponent = (
      <Box sx={{ ...testimonialContainer, ...customStyles }}>
        <Flex sx={imageContainer}>
          {image && (
            <Box sx={employeeImage}>
              <GatsbyImage
                image={image}
                alt={imageAltText}
                style={{ width: "100%", height: "100%" }}
              />
            </Box>
          )}
          {imageUrl && (
            <Box sx={employeeImage}>
              <Image
                src={imageUrl}
                alt={imageAltText}
                sx={{ width: "100%", height: "100%" }}
              />
            </Box>
          )}
          {companyLogoUrl && (
            <Box sx={companyLogoDefaultBox}>
              <Image
                src={companyLogoUrl}
                alt={companyLogoAltText}
                sx={{ width: "100%", height: "100%" }}
              />
            </Box>
          )}
          {companyLogo && (
            <Box sx={companyLogoDefaultBox}>
              <GatsbyImage
                image={companyLogo}
                alt={companyLogoAltText}
                imgStyle={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </Flex>
        <Text
          sx={feedbackText}
          dangerouslySetInnerHTML={{ __html: feedback }}
        />
        <Divider sx={divider} />
        <Flex sx={infoContainer}>
          <Flex sx={employeeDetails}>
            <Text sx={employeeNameStyles}>{employeeName}</Text>
            <Text sx={employeeDesignationStyles}>{employeeDesignation}</Text>
            {companyName && (
              <Text sx={empCompanyNameStyles}>{companyName}</Text>
            )}
          </Flex>
          {rating && <StarRating rating={rating} />}
        </Flex>
      </Box>
    );
  } else if (variant === "primary") {
    renderedComponent = (
      <Box sx={{ ...testimonialBox, ...customStyles }}>
        <Flex sx={flexColumn}>
          <Box>
            <Flex sx={infoContainer}>
              {rating && <StarRating rating={rating} />}
              {companyLogo && (
                <Box sx={companyLogoBox}>
                  <GatsbyImage
                    image={companyLogo}
                    alt={companyLogoAltText}
                    imgStyle={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
              {companyLogoUrl && (
                <Box sx={companyLogoBox}>
                  <Image
                    src={companyLogoUrl}
                    alt={companyLogoAltText}
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
              )}
            </Flex>
            <Divider sx={dividerV2} />
            <Text
              sx={feedbackTextV2}
              dangerouslySetInnerHTML={{ __html: feedback }}
            />
          </Box>

          <Flex sx={employeeProfile}>
            {image && (
              <Box sx={employeeImageV2}>
                <GatsbyImage
                  image={image}
                  alt={imageAltText}
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            {imageUrl && (
              <Box sx={employeeImageV2}>
                <Image
                  src={imageUrl}
                  alt={imageAltText}
                  sx={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            <Flex sx={employeeDetails}>
              <Text sx={employeeNameStyles}>{employeeName}</Text>
              <Text sx={employeeDesignationStyles}>{employeeDesignation}</Text>
              {companyName && (
              <Text sx={empCompanyNameStyles}>{companyName}</Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    );
  } else if (variant === "secondary") {
    if (subVariant === "secondaryOne") {
      renderedComponent = (
        <Box
          sx={{
            ...testimonialBox,
            p: "1rem",
            backgroundColor: colors.primary100,
            width: ["15.625rem", "15.625rem", "17.5rem", "25.813rem"],
            ...customStyles,
          }}
        >
          <Flex sx={{ ...flexColumn, gap: "3rem", ...innerContainerCustomStyles }}>
            <Box>
              <Flex
                sx={{
                  ...infoContainer,
                  maxWidth: ["13.625rem", "13.625rem", "15.5rem", "23.813rem"],
                }}
              >
                {rating && <StarRating rating={rating} />}
                {companyLogo && (
                  <Box sx={companyLogoBox}>
                    <GatsbyImage
                      image={companyLogo}
                      alt={companyLogoAltText}
                      imgStyle={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
                {companyLogoUrl && (
                  <Box sx={companyLogoBox}>
                    <Image
                      src={companyLogoUrl}
                      alt={companyLogoAltText}
                      sx={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
              </Flex>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Image
                  src={icon}
                  alt="quote"
                  sx={{
                    width: "4.75rem",
                    height: "12.563rem",
                    position: "absolute",
                    top: "-5rem",
                    left: "-1rem",
                    zIndex: -1,
                  }}
                />
                <Text
                  sx={{
                    ...feedbackTextV3,
                    fontSize: [
                      typography.textMd500.fontSize,
                      typography.textMd500.fontSize,
                      typography.textMd500.fontSize,
                      typography.textLg500.fontSize,
                    ],
                    lineHeight: [
                      typography.textMd500.lineHeight,
                      typography.textMd500.lineHeight,
                      typography.textMd500.lineHeight,
                      typography.textLg500.lineHeight,
                    ],
                    p: { m: 0 },
                    mt: "1.25rem",
                  }}
                  dangerouslySetInnerHTML={{ __html: feedback }}
                />
              </Box>
            </Box>
            <Box sx={{ height: "100%" }}>
              <Flex sx={{ height: "100%" }}>
                {image && (
                  <Box sx={employeeImageV2}>
                    <GatsbyImage
                      image={image}
                      alt={imageAltText}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
                {imageUrl && (
                  <Box sx={employeeImageV2}>
                    <Image
                      src={imageUrl}
                      alt={imageAltText}
                      sx={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
                <Flex sx={employeeDetails}>
                  <Text sx={employeeNameStyles}>{employeeName}</Text>
                  <Text sx={employeeDesignationStyles}>
                    {employeeDesignation}
                  </Text>
                  <Text sx={empCompanyNameStyles}>{companyName}</Text>
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Box>
      );
    } else if (subVariant === "secondaryTwo") {
      renderedComponent = (
        <Box
          sx={{
            ...testimonialBox,
            width: ["11.25rem", "11.25rem", "11.25rem", "14.875rem"],
            ...customStyles,
          }}
        >
          <Flex sx={flexColumn}>
            <Box>
              <Flex sx={infoContainer}>
                {rating && <StarRating rating={rating} />}
                {companyLogo && (
                  <Box sx={companyLogoBox}>
                    <GatsbyImage
                      image={companyLogo}
                      alt={companyLogoAltText}
                      imgStyle={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
                {companyLogoUrl && (
                  <Box sx={companyLogoBox}>
                    <Image
                      src={companyLogoUrl}
                      alt={companyLogoAltText}
                      sx={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
              </Flex>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Image
                  src={icon}
                  alt="quote"
                  sx={{
                    width: "4.75rem",
                    height: "12rem",
                    position: "absolute",
                    top: "-5rem",
                    left: "-1rem",
                    zIndex: -1,
                  }}
                />
                <Text
                  sx={{ ...feedbackTextV3 }}
                  dangerouslySetInnerHTML={{ __html: feedback }}
                />
              </Box>
            </Box>
            <Flex sx={employeeProfile}>
              {image && (
                <Box sx={employeeImageV2}>
                  <GatsbyImage
                    image={image}
                    alt={imageAltText}
                    style={{ width: "100%", height: "100%" }}
                  />
                </Box>
              )}
              {imageUrl && (
                <Box sx={employeeImageV2}>
                  <Image
                    src={imageUrl}
                    alt={imageAltText}
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
              )}
              <Flex sx={employeeDetails}>
                <Text sx={employeeNameStyles}>{employeeName}</Text>
                <Text sx={employeeDesignationStyles}>
                  {employeeDesignation}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      );
    }
  } else if (variant === "banner") {
    renderedComponent = (
      <Box
        sx={{
          backgroundColor: backgroundColor,
          ...bannerContainer,
          ...customStyles,
        }}
      >
        <Box>
          {rating && (
            <StarRating
              rating={rating}
              customStyles={{
                justifyContent: "center",
              }}
            />
          )}
          <Box
            sx={{
              mt: ["1rem", "1rem", "1rem", "1.5rem"],
              textAlign: "center",
            }}
          >
            <Text sx={{...typography.h3, color:colors.primaryMain}}>"</Text>
            <Text
              sx={bannerFeedback}
              dangerouslySetInnerHTML={{ __html: feedback }}
            />
            <Text sx={{...typography.h3, color:colors.primaryMain}}>"</Text>
          </Box>
          <Flex
            sx={{
              mt: ["1rem", "1rem", "1rem", "1.5rem"],
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {image && (
              <Box sx={employeeImageV3}>
                <GatsbyImage
                  image={image}
                  alt={imageAltText}
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            {imageUrl && (
              <Box sx={employeeImageV3}>
                <Image
                  src={imageUrl}
                  alt={imageAltText}
                  sx={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            <Flex sx={employeeDetails}>
              <Text sx={bannerEmployeeName}>{employeeName}</Text>
              <Text sx={bannerEmployeeDesignation}>{employeeDesignation}</Text>
              {companyName && (
                <Text sx={bannerEmployeeDesignation}>{companyName}</Text>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>
    );
  }

  const handleLinkClick = (e) => {
    if (!e.target.closest(".testimonial-content")) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (reviewUrl) {
    return (
      <TestimonialWrapper
        onClick={handleLinkClick}
        style={wrapper1CustomeStyles}
      >
        <StyledLink href={reviewUrl} target="_blank" rel="noopener noreferrer">
          <div style={wrapper2CustomeStyles} className="testimonial-content">
            {renderedComponent}
          </div>
        </StyledLink>
      </TestimonialWrapper>
    );
  } else {
    return renderedComponent;
  }
};

const {
  testimonialContainer,
  imageContainer,
  employeeImage,
  employeeImageV2,
  feedbackText,
  feedbackTextV2,
  feedbackTextV3,
  divider,
  dividerV2,
  employeeDetails,
  employeeNameStyles,
  employeeDesignationStyles,
  empCompanyNameStyles,
  infoContainer,
  testimonialBox,
  flexColumn,
  companyLogoBox,
  employeeProfile,
  companyLogoDefaultBox,
  employeeImageV3,
  bannerContainer,
  bannerFeedback,
  bannerEmployeeName,
  bannerEmployeeDesignation,
} = {
  testimonialContainer: {
    position: "relative",
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "20px",
    padding: [
      "4.25rem 2.313rem 2.37rem",
      "4.25rem 2.313rem 2.37rem",
      "4.188rem 4.375rem 3.563rem",
      "4.188rem 4.375rem 3.563rem",
    ],
    position: "relative",
    width: "100%",
    maxWidth: ["100%", "100%", "35.625rem", "35.625rem"],
  },
  imageContainer: { alignItems: "center", justifyContent: "center" },
  employeeImage: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    position: "absolute",
    top: "-50px",
    left: ["30%", "25%", "20%", "20%"],
    transform: "translateX(-50%)",
    overflow: "hidden",
  },
  employeeImageV2: {
    width: "53px",
    height: "53px",
    borderRadius: "50%",
    overflow: "hidden",
    mr: "1rem",
  },
  feedbackText: {
    ...typography.textMd400,
    display: "inline-block",
    minHeight: "6rem",
  },
  feedbackTextV2: {
    ...typography.textMd400,
    display: "inline-block",
  },
  feedbackTextV3: {
    ...typography.textLg500,
    display: "inline-block",
  },
  divider: {
    mt: "2rem",
    mb: "1.5rem",
    pb: 0,
    borderColor: colors.neutral200,
  },
  dividerV2: {
    my: "1.5rem",
    pb: 0,
    borderColor: colors.neutral200,
  },
  employeeDetails: {
    flexDirection: "column",
  },
  employeeNameStyles: {
    ...typography.textMd500,
    mb: "0.25rem",
  },
  employeeDesignationStyles: {
    ...typography.textMd400,
    color: colors.neutral700,
  },
  empCompanyNameStyles: {
    ...typography.textMd400,
    color: colors.neutral700,
    mt: "0.25rem",
  },
  infoContainer: {
    position: "relative",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    flexWrap: ["wrap-reverse", "wrap-reverse", "nowrap", "nowrap"],
  },
  testimonialBox: {
    backgroundColor: colors.white,
    borderRadius: "20px",
    border: `1px solid ${colors.neutral200}`,
    padding: "32px 16px",
    width: "100%",
  },
  flexColumn: {
    flexDirection: "column",
    height: "100%",
    justifyContent: "space-between",
  },
  companyLogoBox: {
    position: "absolute",
    top: "-0.5rem",
    right: "0rem",
  },
  companyLogoDefaultBox: {
    position: "absolute",
    top: "1rem",
    right: "2rem",
  },
  employeeProfile: {
    alignItems: "center",
    mt: "3rem",
  },
  employeeImageV3: {
    width: ["2.5rem", "2.5rem", "3.25rem", "3.75rem"],
    height: ["2.5rem", "2.5rem", "3.25rem", "3.75rem"],
    borderRadius: "50%",
    overflow: "hidden",
    mr: ["0.75rem", "0.75rem", "1rem", "1rem"],
  },
  bannerContainer: {
    maxWidth: "56.25rem",
    width: ["18.75rem", "18.75rem", "43.75rem", "56.25rem"],
    borderRadius: "0.75rem",
    p: ["1rem", "1rem", "1.5rem", "3rem"],
  },
  bannerFeedback: {
    fontFamily: "Open Sans",
    fontWeight: [
      typography.textMd400.fontWeight,
      typography.textMd400.fontWeight,
      typography.textMd500.fontWeight,
      typography.textLg500.fontWeight,
    ],
    fontSize: [
      typography.textMd400.fontSize,
      typography.textMd400.fontSize,
      typography.textMd500.fontSize,
      typography.textLg500.fontSize,
    ],
    lineHeight: [
      typography.textMd400.lineHeight,
      typography.textMd400.lineHeight,
      typography.textMd500.lineHeight,
      typography.textLg500.lineHeight,
    ],
    p: { m: 0, display: "inline", },
  },
  bannerEmployeeName: {
    fontFamily: [
      typography.textSm.fontFamily,
      typography.textSm.fontFamily,
      typography.textMd400.fontFamily,
      typography.h7.fontFamily,
    ],
    fontWeight: [
      typography.textSm.fontWeight,
      typography.textSm.fontWeight,
      typography.textMd400.fontWeight,
      typography.h7.fontWeight,
    ],
    fontSize: [
      typography.textSm.fontSize,
      typography.textSm.fontSize,
      typography.textMd400.fontSize,
      typography.h7.fontSize,
    ],
    lineHeight: [
      typography.textSm.lineHeight,
      typography.textSm.lineHeight,
      typography.textMd400.lineHeight,
      typography.h7.lineHeight,
    ],
  },
  bannerEmployeeDesignation: {
    color: colors.neutral900,
    fontFamily: "Open Sans",
    fontWeight: [
      typography.textXs.fontWeight,
      typography.textXs.fontWeight,
      typography.textSm.fontWeight,
      typography.textSm.fontWeight,
    ],
    fontSize: [
      typography.textXs.fontSize,
      typography.textXs.fontSize,
      typography.textSm.fontSize,
      typography.textSm.fontSize,
    ],
    lineHeight: [
      typography.textXs.lineHeight,
      typography.textXs.lineHeight,
      typography.textSm.lineHeight,
      typography.textSm.lineHeight,
    ],
  },
};

Testimonial.propTypes = {
  variant: PropTypes.oneOf(["default", "primary", "secondary"]).isRequired,
  subVariant: PropTypes.oneOf(["secondaryOne", "secondaryTwo"]).isRequired,
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  reviewUrl: PropTypes.string,
  companyName: PropTypes.string,
  companyLogo: PropTypes.object,
  companyLogoUrl: PropTypes.string,
  companyLogoAltText: PropTypes.string,
  imageAltText: PropTypes.string,
  feedback: PropTypes.string.isRequired,
  employeeName: PropTypes.string,
  employeeDesignation: PropTypes.string,
  rating: PropTypes.number.isRequired,
  backgroundColor: PropTypes.string
};

Testimonial.defaultProps = {
  variant: "default",
  imageUrl: "",
  companyLogoUrl: "",
  companyLogoAltText: "",
  imageAltText: "",
  feedback: "",
  employeeName: "",
  employeeDesignation: "",
  backgroundColor: colors.primary100
};

export default Testimonial;
